import type { FederalTaxInput, FederalTaxResult, IrsStatus } from '@divorceos/schema';
import { byJoint, byMfs, yearData, type FederalTables, type YearData } from './data.js';
import { cents, clamp0, max, min, scheduleTax } from './schedule.js';
import { amtPartIII, amtRateTax, ordinaryTax, qdcgWorksheet, scheduleDTaxWorksheet, type CapGainSummary, type Lines } from './worksheets.js';

const num = (x: number | undefined | null): number => (typeof x === 'number' && Number.isFinite(x) ? x : 0);
const count = (x: number): number => (x >= 2.9 ? 3 : x >= 1.9 ? 2 : x >= 0.9 ? 1 : 0);
/** ceil() that tolerates floating-point noise (e.g. 2000/2000 = 1.0000000002). */
const ceilSafe = (x: number): number => Math.ceil(x - 1e-9);

interface SeResult { netEarnings: number; ss: number; medicare: number; total: number }

/** Schedule SE for one person. `wagesSs` = that person's W-2 Social Security wages (reduce the SS base). */
function scheduleSE(d: YearData, profit: number, wagesSs: number): SeResult {
  const e = d.employment;
  const net = cents(profit * e.seNetEarningsFactor);
  if (net < e.seThreshold) return { netEarnings: 0, ss: 0, medicare: 0, total: 0 };
  const ss = cents(e.seSsRate * min(net, clamp0(e.ssWageBase - wagesSs)));
  const medicare = cents(e.seMedicareRate * net);
  return { netEarnings: net, ss, medicare, total: cents(ss + medicare) };
}

interface EicParams { children: number; maxCredit: number; phaseInEnd: number; start: number; end: number }

/** EIC at one income figure (earned income or AGI) using the $50-band midpoint, linear phase-in / phase-out. */
function eicAt(p: EicParams, x: number): number {
  if (x <= 0) return 0;
  const mid = Math.floor(x / 50) * 50 + 25;
  if (mid >= p.end) return 0;
  if (mid < p.phaseInEnd) return cents(min(p.maxCredit, (p.maxCredit * mid) / p.phaseInEnd));
  if (mid <= p.start) return p.maxCredit;
  return cents((p.maxCredit * (p.end - mid)) / (p.end - p.start));
}

/** An all-zero input (switches on) for the given year/status; spread overrides on top of it. */
export function emptyFederalTaxInput(year: FederalTaxInput['year'], status: IrsStatus): FederalTaxInput {
  return {
    year, status,
    wages: 0, medicareWages: 0, ssWages: 0, spouseWages: 0, spouseSelfEmployment: 0,
    taxableInterest: 0, taxExemptInterest: 0, ordinaryDividends: 0, qualifiedDividends: 0,
    capGainShort: 0, capGainLong: 0, rateGain28: 0, unrecaptured1250: 0,
    selfEmployment: 0, scheduleE: 0, otherIncome: 0, unemployment: 0, socialSecurityBenefits: 0, alimonyReceivedTaxable: 0,
    sepSimple: 0, otherAdjustments: 0, alimonyPaidDeductible: 0,
    medicalExpenses: 0, realEstateTax: 0, homeMortgageInterest: 0, investmentInterest: 0, form4952Line4e: 0, form4952Line4g: 0,
    charitable: 0, miscDeductions: 0, stateSalesTax: 0, forceItemize: false, forceStandard: false, qbid: 0,
    dependents: 0, qualifyingChildrenCtc: 0, qualifyingChildrenEic: 0, qualifyingChildrenCcc: 0, otherDependents: 0,
    age65Count: 0, blindCount: 0, childCareExpenses: 0, earnedIncome: 0, spouseEarnedIncome: 0,
    amtHomeMortgageInterest: 0, amtInvestmentInterest: 0, amtDepletion: 0, amtNol: 0, amtOtherAdjustments: 0,
    mfsLivedTogether: false, mfsQualifiedForEitc: false, otherNonrefundableCredits: 0, otherRefundableCredits: 0,
    includeSocialSecurityTax: true, includeMedicareTax: true, includeSeTax: true,
    includeChildCareCredit: true, includeEic: true, includeCtc: true,
    stateIncomeTaxPaid: 0, stateSdiPaid: 0,
  };
}

/** Missing / NaN numeric fields become 0 so partial inputs cannot poison the arithmetic. */
function normalizeInput(input: FederalTaxInput): FederalTaxInput {
  const out = emptyFederalTaxInput(input.year, input.status) as unknown as Record<string, unknown>;
  for (const [k, v] of Object.entries(input)) {
    if (typeof out[k] === 'number') out[k] = num(v as number);
    else if (v !== undefined) out[k] = v;
  }
  return out as unknown as FederalTaxInput;
}

export interface FederalTaxOptions { tables?: FederalTables }

/** Full federal return for one filing unit. All amounts annual dollars; results rounded to cents. */
export function computeFederalTax(input: FederalTaxInput, opts: FederalTaxOptions = {}): FederalTaxResult {
  const d = yearData(input.year, opts.tables ?? 'irs');
  const st: IrsStatus = input.status;
  const joint = st === 'MFJ';
  const married = st === 'MFJ' || st === 'MFS';
  const L: Lines = {};
  const set = (k: string, v: number): number => { L[k] = cents(v); return L[k]; };
  const inp = normalizeInput(input);

  // ---- Form 1040 income lines -------------------------------------------------------------
  const wages = set('1040.1z', inp.wages);
  const taxExemptInterest = set('1040.2a', inp.taxExemptInterest);
  const taxableInterest = set('1040.2b', inp.taxableInterest);
  const qualifiedDividends = set('1040.3a', min(inp.qualifiedDividends, inp.ordinaryDividends));
  const ordinaryDividends = set('1040.3b', inp.ordinaryDividends);

  // ---- Schedule SE (party, and spouse when joint) -------------------------------------------
  const seParty = inp.includeSeTax ? scheduleSE(d, inp.selfEmployment, inp.ssWages) : scheduleSE(d, 0, 0);
  const seSpouse = inp.includeSeTax && joint ? scheduleSE(d, inp.spouseSelfEmployment, inp.spouseWages) : scheduleSE(d, 0, 0);
  set('SchSE.3', inp.selfEmployment);
  set('SchSE.4', seParty.netEarnings);
  set('SchSE.10', seParty.ss);
  set('SchSE.11', seParty.medicare);
  set('SchSE.12', seParty.total);
  set('SchSE.13', seParty.total / 2);
  if (joint) {
    set('SchSE.spouse.3', inp.spouseSelfEmployment);
    set('SchSE.spouse.4', seSpouse.netEarnings);
    set('SchSE.spouse.12', seSpouse.total);
  }
  const seTax = set('Sch2.4', seParty.total + seSpouse.total);
  const seNetEarnings = seParty.netEarnings + seSpouse.netEarnings;

  // ---- Schedule 1 income ------------------------------------------------------------------
  const seProfit = inp.selfEmployment + (joint ? inp.spouseSelfEmployment : 0);
  set('Sch1.2a', inp.alimonyReceivedTaxable);
  set('Sch1.3', seProfit);
  set('Sch1.5', inp.scheduleE);
  set('Sch1.7', inp.unemployment);
  set('Sch1.8z', inp.otherIncome);
  const sched1Income = set('Sch1.10', inp.alimonyReceivedTaxable + seProfit + inp.scheduleE + inp.unemployment + inp.otherIncome);
  set('1040.8', sched1Income);

  // ---- Schedule 1 adjustments ---------------------------------------------------------------
  const halfSe = set('Sch1.15', seTax / 2);
  set('Sch1.16', inp.sepSimple);
  set('Sch1.19a', inp.alimonyPaidDeductible);
  set('Sch1.24z', inp.otherAdjustments);
  const adjustments = set('Sch1.26', halfSe + inp.sepSimple + inp.alimonyPaidDeductible + inp.otherAdjustments);
  set('1040.10', adjustments);

  // ---- Schedule D summary -------------------------------------------------------------------
  const netShort = set('SchD.7', inp.capGainShort);
  const netLong = set('SchD.15', inp.capGainLong);
  const netGain = set('SchD.16', netShort + netLong);
  const lossLimit = byMfs(d.capitalLossLimit, st);
  let capGainLine7 = 0;
  if (netGain > 0) capGainLine7 = netGain;
  else if (netGain < 0) capGainLine7 = -min(-netGain, lossLimit);
  set('SchD.21', netGain < 0 ? capGainLine7 : 0);
  const hasLtGain = netLong > 0 && netGain > 0;
  const rateGain28 = set('SchD.18', hasLtGain ? clamp0(inp.rateGain28) : 0);
  const unrecaptured1250 = set('SchD.19', hasLtGain ? clamp0(inp.unrecaptured1250) : 0);
  set('1040.7', capGainLine7);
  const useSchDWks = inp.form4952Line4g > 0 || (hasLtGain && (rateGain28 > 0 || unrecaptured1250 > 0));
  const useQdcg = !useSchDWks && (qualifiedDividends > 0 || hasLtGain);

  // ---- Social Security benefits worksheet ---------------------------------------------------
  const benefits = set('1040.6a', inp.socialSecurityBenefits);
  const incomeExclSs = wages + taxableInterest + ordinaryDividends + capGainLine7 + sched1Income;
  let taxableSs = 0;
  if (benefits > 0) {
    const ss = d.socialSecurityBenefits;
    const mfsTogether = st === 'MFS' && inp.mfsLivedTogether;
    const w1 = benefits;
    const w2 = w1 * ss.rate1;
    const w3 = incomeExclSs;
    const w4 = taxExemptInterest;
    const w5 = w2 + w3 + w4;
    const w6 = adjustments;
    const w7 = w5 - w6;
    const w8 = mfsTogether ? 0 : byJoint(ss.base, st);
    const w9 = w7 - w8;
    set('SSWks.1', w1); set('SSWks.5', w5); set('SSWks.7', w7); set('SSWks.9', w9);
    if (w9 > 0) {
      const w10 = mfsTogether ? 0 : byJoint(ss.secondTier, st);
      const w11 = clamp0(w9 - w10);
      const w12 = min(w9, w10);
      const w13 = w12 * ss.rate1;
      const w14 = min(w2, w13);
      const w15 = w11 * ss.rate2;
      const w16 = w14 + w15;
      const w17 = w1 * ss.rate2;
      taxableSs = cents(min(w16, w17));
      set('SSWks.11', w11); set('SSWks.14', w14); set('SSWks.15', w15); set('SSWks.16', w16); set('SSWks.17', w17);
    }
    set('SSWks.18', taxableSs);
  }
  set('1040.6b', taxableSs);

  // ---- Total income / AGI -------------------------------------------------------------------
  const totalIncome = set('1040.9', incomeExclSs + taxableSs);
  const agi = set('1040.11', totalIncome - adjustments);

  // ---- Standard deduction (with 65/blind extras) --------------------------------------------
  const boxCap = married ? 4 : 2;
  const boxes = min(boxCap, clamp0(inp.age65Count) + clamp0(inp.blindCount));
  const extraPerBox = married ? d.additionalStandardDeduction.married : d.additionalStandardDeduction.unmarried;
  const standardDeduction = set('SchA.std', d.standardDeduction[st] + boxes * extraPerBox);

  // ---- Schedule A ---------------------------------------------------------------------------
  const medicalFloor = set('SchA.3', d.scheduleA.medicalFloor * clamp0(agi));
  const medical = set('SchA.4', clamp0(set('SchA.1', inp.medicalExpenses) - medicalFloor));
  const stateIncomeTaxes = inp.stateIncomeTaxPaid + inp.stateSdiPaid;
  const stateTaxes = set('SchA.5a', max(stateIncomeTaxes, inp.stateSalesTax));
  const realEstateTax = set('SchA.5b', inp.realEstateTax);
  const saltBeforeCap = set('SchA.5d', stateTaxes + realEstateTax);
  let saltCap = byMfs(d.scheduleA.saltCap, st);
  const pd = d.scheduleA.saltCapPhaseDown;
  if (pd) saltCap = max(byMfs(pd.floor, st), saltCap - pd.rate * clamp0(agi - byMfs(pd.start, st)));
  const salt = set('SchA.5e', min(saltBeforeCap, saltCap));
  set('SchA.7', salt);
  const mortgageInterest = set('SchA.8a', inp.homeMortgageInterest);
  // Form 4952: investment interest limited to net investment income (interest + non-qualified dividends + elected 4g).
  const netInvestmentIncome4952 = clamp0(taxableInterest + (ordinaryDividends - qualifiedDividends) + clamp0(inp.form4952Line4g));
  set('4952.6', netInvestmentIncome4952);
  const investmentInterest = set('SchA.9', min(clamp0(inp.investmentInterest), netInvestmentIncome4952));
  set('4952.8', investmentInterest);
  const interest = set('SchA.10', mortgageInterest + investmentInterest);
  const charitable = set('SchA.14', inp.charitable);
  const misc = set('SchA.16', inp.miscDeductions);
  const itemizedDeduction = set('SchA.17', medical + salt + interest + charitable + misc);

  let itemizing: boolean;
  if (inp.forceItemize && !inp.forceStandard) itemizing = true;
  else if (inp.forceStandard) itemizing = false;
  else itemizing = itemizedDeduction > standardDeduction;
  let deduction = itemizing ? itemizedDeduction : standardDeduction;
  set('1040.12', deduction);

  // 2025+ senior deduction (Schedule 1-A), available whether or not itemizing.
  let seniorDeduction = 0;
  if (d.seniorDeduction && inp.age65Count > 0) {
    const sd = d.seniorDeduction;
    const persons = min(joint ? 2 : 1, inp.age65Count);
    seniorDeduction = cents(clamp0(sd.amount * persons - sd.phaseOutRate * clamp0(agi - byJoint(sd.phaseOutStart, st))));
    set('Sch1A.senior', seniorDeduction);
    set('1040.13b', seniorDeduction);
    deduction = cents(deduction + seniorDeduction);
  }

  // ---- QBID / taxable income ----------------------------------------------------------------
  const qbid = set('1040.13', min(clamp0(inp.qbid), 0.2 * clamp0(agi - deduction)));
  set('1040.13a', qbid);
  set('1040.14', deduction + qbid);
  const taxableSigned = agi - deduction - qbid;
  const taxableIncome = set('1040.15', clamp0(taxableSigned));

  // ---- Regular tax (line 16) ----------------------------------------------------------------
  const cgInputs = {
    taxableIncome, qualifiedDividends, netLongTerm: netLong, netGain, rateGain28, unrecaptured1250,
    form4952Line4e: inp.form4952Line4e, form4952Line4g: inp.form4952Line4g,
  };
  let cgSummary: CapGainSummary | null = null;
  let taxBeforeCredits: number;
  if (useSchDWks) {
    cgSummary = scheduleDTaxWorksheet(d, st, cgInputs, L);
    taxBeforeCredits = cgSummary.tax;
  } else if (useQdcg) {
    cgSummary = qdcgWorksheet(d, st, cgInputs, L);
    taxBeforeCredits = cgSummary.tax;
  } else {
    taxBeforeCredits = ordinaryTax(d, st, taxableIncome);
  }
  set('1040.16', taxBeforeCredits);
  // Marginal rate (F+0x12c): the bracket lookup's rate — of the ordinary-income portion when a capital-gain
  // worksheet computed the tax (the worksheet's own lookup, QD&CG line 5 / Sch D Tax Wks line 14).
  const marginalBase = cgSummary ? cgSummary.ordinaryPortion : taxableIncome;
  const marginalRate = marginalBase > 0 ? scheduleTax(d.bracketTops[st], d.rates, marginalBase).marginalRate : 0;

  // ---- Form 6251 AMT ------------------------------------------------------------------------
  const amtLine1 = taxableIncome > 0 ? taxableIncome : taxableSigned; // per the form: may be negative
  const amtLine2a = itemizing ? salt : standardDeduction;
  let amti = amtLine1 + amtLine2a + inp.amtHomeMortgageInterest + inp.amtInvestmentInterest + inp.amtDepletion
    + inp.amtOtherAdjustments - Math.abs(inp.amtNol);
  const amtEx = d.amt.exemption[st];
  const amtStart = d.amt.phaseOutStart[st];
  if (st === 'MFS') {
    const fullyPhasedOut = amtStart + amtEx / d.amt.phaseOutRate;
    if (amti > fullyPhasedOut) amti += min(amtEx, d.amt.phaseOutRate * (amti - fullyPhasedOut));
  }
  amti = set('6251.4', amti);
  set('6251.1', amtLine1); set('6251.2a', amtLine2a);
  const amtExemption = set('6251.5', amti <= amtStart ? amtEx : clamp0(amtEx - d.amt.phaseOutRate * (amti - amtStart)));
  const amtLine6 = set('6251.6', clamp0(amti - amtExemption));
  const tentativeMin = set('6251.7', cgSummary ? amtPartIII(d, st, amtLine6, cgSummary, L) : amtRateTax(d, st, amtLine6));
  set('6251.9', tentativeMin);
  set('6251.10', taxBeforeCredits);
  const amt = set('6251.11', clamp0(tentativeMin - taxBeforeCredits));
  set('Sch2.1', amt);
  set('1040.17', amt);
  const line18 = set('1040.18', taxBeforeCredits + amt);

  // ---- Employee FICA ------------------------------------------------------------------------
  const e = d.employment;
  const ficaSocialSecurity = set('FICA.ss', inp.includeSocialSecurityTax ? e.ficaSsRate * min(clamp0(inp.ssWages), e.ssWageBase) : 0);
  const ficaMedicare = set('FICA.medicare', inp.includeMedicareTax ? e.ficaMedicareRate * clamp0(inp.medicareWages) : 0);

  // ---- EIC (Worksheet B) — computed before Schedule 8812 because Part II-B of 8812 uses it -----
  let eic = 0;
  const eicChildren = count(clamp0(inp.qualifyingChildrenEic));
  const eicEarnedIncome = set('EIC.earnedIncome', wages + seProfit - halfSe);
  const eicInvestmentIncome = set('EIC.investmentIncome', taxableInterest + taxExemptInterest + ordinaryDividends + clamp0(capGainLine7) + clamp0(inp.scheduleE));
  const eicAllowed = inp.includeEic && (st !== 'MFS' || inp.mfsQualifiedForEitc) && eicInvestmentIncome <= d.eic.investmentIncomeLimit && eicEarnedIncome > 0;
  if (eicAllowed) {
    const raw = d.eic.byChildren.find((s) => s.children === eicChildren) ?? d.eic.byChildren[d.eic.byChildren.length - 1];
    const p: EicParams = { children: eicChildren, maxCredit: raw.maxCredit, phaseInEnd: raw.phaseInEnd, start: byJoint(raw.phaseOutStart, st), end: byJoint(raw.phaseOutEnd, st) };
    const creditEi = set('EIC.creditEarnedIncome', eicAt(p, eicEarnedIncome));
    eic = creditEi;
    if (agi >= p.start) eic = min(eic, set('EIC.creditAgi', eicAt(p, agi)));
  }
  eic = set('1040.27', eic);

  // ---- Form 2441 child and dependent care credit -------------------------------------------
  let childCareCredit = 0;
  const cccPersons = count(clamp0(inp.qualifyingChildrenCcc));
  if (inp.includeChildCareCredit && cccPersons > 0 && st !== 'MFS') {
    const cc = d.childCareCredit;
    const l3 = min(clamp0(inp.childCareExpenses), cccPersons >= 2 ? cc.capTwoPlus : cc.capOne);
    const l4 = clamp0(inp.earnedIncome);
    const l5 = joint ? clamp0(inp.spouseEarnedIncome) : l4;
    const l6 = min(l3, l4, l5);
    const steps = agi > cc.agiStart ? ceilSafe((agi - cc.agiStart) / cc.stepSize) : 0;
    const rate = max(cc.minRate, cc.maxRate - steps * cc.stepRate);
    const l9 = cents(l6 * rate);
    const l10 = line18;
    childCareCredit = min(l9, l10);
    set('2441.3', l3); set('2441.6', l6); set('2441.8', rate); set('2441.9', l9); set('2441.10', l10);
  }
  childCareCredit = set('2441.11', childCareCredit);
  set('Sch3.2', childCareCredit);
  const otherNonrefundableCredits = set('Sch3.6z', clamp0(inp.otherNonrefundableCredits));
  const sched3 = set('Sch3.8', childCareCredit + otherNonrefundableCredits);
  set('1040.20', sched3);

  // ---- Schedule 8812 CTC / ODC / ACTC -------------------------------------------------------
  let childTaxCredit = 0;
  let additionalChildTaxCredit = 0;
  if (inp.includeCtc) {
    const c = d.childTaxCredit;
    const kids = clamp0(inp.qualifyingChildrenCtc);
    const odc = clamp0(inp.otherDependents);
    const l5 = kids * c.perChild;
    const l7 = odc * c.perOtherDependent;
    const l8 = l5 + l7;
    const thr = c.phaseOutStart[st];
    const l10 = agi > thr ? ceilSafe((agi - thr) / 1000) * 1000 : 0;
    const l11 = (l10 / 1000) * c.phaseOutPer1000;
    const l12 = clamp0(l8 - l11);
    const l13 = clamp0(line18 - sched3);
    const l14 = min(l12, l13);
    childTaxCredit = l14;
    set('8812.4', kids); set('8812.5', l5); set('8812.6', odc); set('8812.7', l7); set('8812.8', l8); set('8812.11', l11);
    set('8812.12', l12); set('8812.13', l13); set('8812.14', l14);
    if (kids > 0 && l12 > l14) {
      const l16a = l12 - l14;
      const l16b = kids * c.actcPerChild;
      const l17 = min(l16a, l16b);
      const l18a = eicEarnedIncome;
      const l19 = clamp0(l18a - c.actcEarnedIncomeFloor);
      const l20 = cents(l19 * c.actcRate);
      set('8812.16a', l16a); set('8812.16b', l16b); set('8812.17', l17); set('8812.18a', l18a); set('8812.20', l20);
      let l26 = l20;
      if (kids >= 3 && l20 < l17) {
        const l21 = ficaSocialSecurity + ficaMedicare;
        const l22 = halfSe;
        const l23 = l21 + l22;
        const l24 = eic;
        const l25 = clamp0(l23 - l24);
        l26 = max(l20, l25);
        set('8812.21', l21); set('8812.23', l23); set('8812.25', l25); set('8812.26', l26);
      }
      additionalChildTaxCredit = min(l17, l26);
    }
  }
  childTaxCredit = set('8812.14', childTaxCredit);
  additionalChildTaxCredit = set('8812.27', additionalChildTaxCredit);
  set('1040.19', childTaxCredit);
  set('1040.28', additionalChildTaxCredit);

  const line21 = set('1040.21', childTaxCredit + sched3);
  const taxAfterCredits = set('1040.22', clamp0(line18 - line21));

  // ---- Form 8959 additional Medicare tax --------------------------------------------------
  const medWages = clamp0(inp.medicareWages) + (joint ? clamp0(inp.spouseWages) : 0);
  const amThr = e.additionalMedicareThreshold[st];
  const f8959Line7 = set('8959.7', e.additionalMedicareRate * clamp0(medWages - amThr));
  const f8959Line12 = clamp0(seNetEarnings - clamp0(amThr - medWages));
  const f8959Line13 = set('8959.13', e.additionalMedicareRate * f8959Line12);
  const additionalMedicareTax = set('8959.18', f8959Line7 + f8959Line13);
  set('Sch2.11', additionalMedicareTax);

  // ---- Form 8960 net investment income tax --------------------------------------------------
  const nii = set('8960.8', taxableInterest + ordinaryDividends + inp.scheduleE + capGainLine7);
  let niit = 0;
  if (nii > 0) {
    const l9a = itemizing ? investmentInterest : 0;
    // Line 9b (state income tax allocable to NII), as the original us_tax.dll does it: the state income tax
    // (net of credits) x min(1, NII / state taxable income), capped at the SALT deduction actually allowed
    // (Schedule A line 5e) — applied whether or not the return itemizes. Falls back to an NII / AGI share
    // when the state taxable income is not supplied.
    const stateTaxableIncome = num(input.stateTaxableIncome);
    const share = stateTaxableIncome > 0 ? min(1, nii / stateTaxableIncome) : agi > 0 ? min(1, nii / agi) : 0;
    const l9b = min(salt, clamp0(inp.stateIncomeTaxPaid) * share);
    const l11 = set('8960.11', l9a + l9b);
    const l12 = set('8960.12', clamp0(nii - l11));
    const l13 = set('8960.13', agi);
    const l14 = set('8960.14', e.niitThreshold[st]);
    const l15 = set('8960.15', clamp0(l13 - l14));
    const l16 = set('8960.16', min(l12, l15));
    niit = cents(l16 * e.niitRate);
  }
  niit = set('8960.17', niit);
  set('Sch2.12', niit);

  const otherTaxes = set('Sch2.21', seTax + additionalMedicareTax + niit);
  set('1040.23', otherTaxes);
  const totalTax = set('1040.24', taxAfterCredits + otherTaxes);

  // ---- Payments / refundable credits ----------------------------------------------------------
  const otherRefundableCredits = set('1040.31', clamp0(inp.otherRefundableCredits));
  const refundable = set('1040.32', eic + additionalChildTaxCredit + otherRefundableCredits);
  set('1040.33', refundable);
  const net = set('1040.net', totalTax - refundable);
  set('1040.34', clamp0(-net));
  set('1040.37', clamp0(net));

  const netIncomeTax = set('netIncomeTax', net - seTax);
  const employmentTax = set('employmentTax', ficaSocialSecurity + ficaMedicare + seParty.total);
  set('marginalRate', marginalRate);

  return {
    totalIncome,
    adjustments,
    agi,
    standardDeduction,
    itemizedDeduction,
    itemizing,
    deduction,
    qbid,
    taxableIncome,
    taxBeforeCredits,
    amt,
    childCareCredit,
    childTaxCredit,
    additionalChildTaxCredit,
    eic,
    otherNonrefundableCredits,
    otherRefundableCredits,
    taxAfterCredits,
    selfEmploymentTax: seTax,
    additionalMedicareTax,
    niit,
    ficaSocialSecurity,
    ficaMedicare,
    netIncomeTax,
    employmentTax,
    marginalRate,
    amtTriggered: amt > 0,
    netCapitalGain: capGainLine7,
    taxableSocialSecurity: taxableSs,
    lines: L,
  };
}
